import React from 'react';

import { editorSection } from '../../pageEditTabs';

import Features from '@dashboard/enums/features';
import getPageTypeLabel from '@dashboard/services/getPageTypeLabel';

import CanUseFeature from '@dashboard/components/CanUseFeature';

import { getEmailsSections } from '../PageEmails';
import { getTrackingSections } from '../PageTracking';

import DynamicPricingForm from './components/DynamicPricingForm';
import GoogleIndexForm from './components/GoogleIndexForm';
import RedirectPageForm from './components/RedirectPageForm';
import SlugForm from './components/SlugForm';
import PopUpBehaviorForm from '@/dashboard/scenes/PageEdit/scenes/PageSettings/components/PopUpBehaviorForm';
import TicketQRCodesForm from './components/TicketQRCodesForm';
import PurchaseLimitsForm from '../PageProduct/components/PurchaseLimitsForm';
import RegistrationLimitsForm from '../PageEvent/components/RegistrationLimitsForm';
import SubmissionLimitsForm from '../PageForm/components/SubmissionLimitsForm';

const LIMITS = {
  checkout: {
    component: <PurchaseLimitsForm />,
    description:
      'Choose whether a customer can purchase or subscribe to this product more than once. Customers are matched by the email address entered at checkout.',
  },
  event: {
    component: <RegistrationLimitsForm />,
    description:
      'Choose whether a guest can register for this event more than once. Guests are matched by the email address entered at checkout.',
  },
  form: {
    component: <SubmissionLimitsForm />,
    description:
      'Choose whether a lead can submit this form more than once. Leads are matched by the email address at submission.',
  },
};

const SETTINGS_ORDER = [
  '/url-slug',
  '/google-index',
  '/redirect-page',
  '/limits',
  '/email-notifications',
  '/ticket-qr-codes',
  '/invoices',
  '/tracking-pixels',
  '/dynamic-info',
  '/pop-up-behavior',
];

const settingsRank = section => {
  const rank = SETTINGS_ORDER.indexOf(section.path);
  return rank === -1 ? SETTINGS_ORDER.length : rank;
};

export const getSettingsSections = ({ page, seller }) =>
  getUnorderedSettingsSections({ page, seller }).sort((a, b) => settingsRank(a) - settingsRank(b));

const getUnorderedSettingsSections = ({ page, seller }) => {
  const pageTypeLabel = getPageTypeLabel(page);
  const context = { page, seller };
  const section = path => editorSection('settings', path, context);

  return [
    {
      ...section('/ticket-qr-codes'),
      component: <TicketQRCodesForm />,
      description: <>Control how ticket QR codes are delivered to attendees.</>,
    },
    {
      ...section('/url-slug'),
      component: <SlugForm />,
      width: '2xl',
      description: `Customize the last part of this ${pageTypeLabel}'s URL.`,
      helpUrl: '/docs/articles/url-slug',
    },
    {
      ...section('/dynamic-info'),
      component: (
        <CanUseFeature feature={Features.DYNAMIC_PRICING}>
          <DynamicPricingForm page={page} />
        </CanUseFeature>
      ),
      width: 'lg',
      description: `Choose which details can be changed through URL parameters in your ${pageTypeLabel} link.`,
      helpUrl:
        page.type === 'form'
          ? '/help/articles/dynamic-price-product-information'
          : '/docs/articles/dynamic-price-product-information',
    },
    {
      ...section('/limits'),
      ...LIMITS[page.type],
    },
    {
      ...section('/google-index'),
      component: <GoogleIndexForm />,
      width: 'lg',
      description: <>Control whether this {pageTypeLabel} can appear in Google search results.</>,
    },
    {
      ...section('/redirect-page'),
      component: <RedirectPageForm />,
      width: 'lg',
      description: (
        <>
          Send visitors to another page instead of this {pageTypeLabel}. Only applies to the page
          link, not pop-ups or embeds.
        </>
      ),
    },
    {
      ...section('/pop-up-behavior'),
      component: <PopUpBehaviorForm />,
      width: 'lg',
      description: <>Control how visitors close the pop-up.</>,
    },
    ...getEmailsSections({ page }),
    ...getTrackingSections({ page }),
  ].filter(tab => tab.isVisible(context));
};
